import { useMemo, useState } from "react";
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isValid,
  parse,
  parseISO,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Calendar, fromJSDate, toJSDate } from "@wellsky/atlas-ui-react";
import type { CalendarDate } from "@wellsky/atlas-ui-react";
import { WorkItem } from "../../types/workItems";
import { mapWorkItemToResidentData } from "../../utils/mdsMapping";
import { MDSRowClickData } from "./MDSAssessmentRow";
import { Popover } from "../ui/popover";

interface MDSCalendarViewProps {
  workItems: WorkItem[];
  onItemClick: (data: MDSRowClickData) => void;
}

// Finished buckets have no clinician work left — mirror CompletionDate so the
// calendar colours completed assessments green rather than "due".
const FINISHED_BUCKETS = [
  "COMPLETED",
  "CANCELLED",
  "STRUCK",
  "PENDING_SUBMISSION",
];

const isFinished = (bucket?: string | null): boolean =>
  !!bucket && FINISHED_BUCKETS.includes(bucket.trim().toUpperCase());

// The calendar is keyed on the Assessment Reference Date (A2300). Items with no
// parseable ARD can't be placed on a day, so they surface in an "Unscheduled"
// list beneath the grid instead of being silently dropped.
//
// The backend serialises assessment_reference_day as MM/DD/YYYY (see the API's
// format_date_for_display), so we must parse that format first — parseISO alone
// returns Invalid Date for MM/DD/YYYY and would dump every scheduled item into
// the "No Assessment Reference Date" bucket. We fall back to parseISO for any
// raw/legacy ISO 8601 values the formatter may have passed through unchanged.
function parseArd(workItem: WorkItem): Date | null {
  const raw = workItem.assessment_reference_day?.trim();
  if (!raw) return null;
  const us = parse(raw, "MM/dd/yyyy", new Date());
  if (isValid(us)) return us;
  const iso = parseISO(raw);
  return isValid(iso) ? iso : null;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// The default window spans the current week plus the next three (four weeks),
// per the CareQueue calendar requirements. Week view narrows to a single week
// as a lighter-weight look at one week's workload.
type ViewMode = "4weeks" | "week";
const WEEKS_IN_WINDOW: Record<ViewMode, number> = { "4weeks": 4, week: 1 };

// When a single resident has more than this many assessments on one day, the
// day cell collapses them into one chip that opens a popup listing them all —
// otherwise a busy resident overflows the cell. (Requirement: same resident
// with more than 8 items on a day → view in a popup.)
const RESIDENT_DAY_COLLAPSE_THRESHOLD = 8;

interface DayPopup {
  residentName: string;
  dateLabel: string;
  items: WorkItem[];
}

export function MDSCalendarView({ workItems, onItemClick }: MDSCalendarViewProps) {
  // Default to the current week so "this week + next three weeks" is what the
  // user sees first; navigation pages the window backward/forward from here.
  const [anchor, setAnchor] = useState<Date>(() =>
    startOfWeek(new Date()),
  );
  const [viewMode, setViewMode] = useState<ViewMode>("4weeks");
  const [popup, setPopup] = useState<DayPopup | null>(null);
  // Controls the "jump to date" calendar popover in the header.
  const [pickerOpen, setPickerOpen] = useState(false);

  // Bucket work items by ARD day (yyyy-MM-dd) for O(1) lookup per cell.
  const { itemsByDay, unscheduled } = useMemo(() => {
    const byDay = new Map<string, WorkItem[]>();
    const none: WorkItem[] = [];
    for (const item of workItems) {
      const ard = parseArd(item);
      if (!ard) {
        none.push(item);
        continue;
      }
      const key = format(ard, "yyyy-MM-dd");
      const bucket = byDay.get(key);
      if (bucket) bucket.push(item);
      else byDay.set(key, [item]);
    }
    return { itemsByDay: byDay, unscheduled: none };
  }, [workItems]);

  const days = useMemo(() => {
    const windowStart = startOfWeek(anchor);
    const windowEnd = endOfWeek(
      addWeeks(windowStart, WEEKS_IN_WINDOW[viewMode] - 1),
    );
    return eachDayOfInterval({ start: windowStart, end: windowEnd });
  }, [anchor, viewMode]);

  const rangeLabel = useMemo(() => {
    const start = days[0];
    const end = days[days.length - 1];
    const sameYear = start.getFullYear() === end.getFullYear();
    const startLabel = format(start, sameYear ? "MMM d" : "MMM d, yyyy");
    return `${startLabel} – ${format(end, "MMM d, yyyy")}`;
  }, [days]);

  const today = new Date();

  const handleItemClick = (workItem: WorkItem) => {
    onItemClick({ row: mapWorkItemToResidentData(workItem), fullWorkItem: workItem });
  };

  const step = (direction: -1 | 1) =>
    setAnchor((a) =>
      direction === 1
        ? addWeeks(a, WEEKS_IN_WINDOW[viewMode])
        : subWeeks(a, WEEKS_IN_WINDOW[viewMode]),
    );

  // Picking a day in the header calendar jumps the window so it starts on the
  // week containing that day — far quicker than paging with the chevrons.
  const handleJumpTo = (value: CalendarDate | null) => {
    if (!value) return;
    setAnchor(startOfWeek(toJSDate(value)));
    setPickerOpen(false);
  };

  const jumpTrigger = (
    <button
      type="button"
      aria-label="Jump to date"
      className="flex items-center gap-1.5 rounded px-2 py-1 hover:bg-muted-foreground/10"
    >
      <CalendarDays className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-semibold">{rangeLabel}</span>
      <ChevronDown className="h-3.5 w-3.5 opacity-70" />
    </button>
  );

  // Group a day's items by resident so we can collapse any resident that blows
  // past the threshold into a single "view popup" chip, while residents with a
  // handful of items keep rendering inline.
  const groupByResident = (dayItems: WorkItem[]): [string, WorkItem[]][] => {
    const groups = new Map<string, WorkItem[]>();
    for (const item of dayItems) {
      const name = item.resident_name || "Unknown resident";
      const arr = groups.get(name);
      if (arr) arr.push(item);
      else groups.set(name, [item]);
    }
    return [...groups.entries()];
  };

  return (
    <div className="relative border-2 border-solid border-border rounded-[var(--radius)] overflow-hidden flex-1 min-h-0 flex flex-col">
      {/* Window navigation + view toggle */}
      <div className="flex items-center justify-between gap-2 px-4 py-2 bg-muted border-b border-border shrink-0">
        <button
          type="button"
          onClick={() => step(-1)}
          className="p-1 rounded hover:bg-muted-foreground/10"
          aria-label="Previous period"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3">
          <Popover
            trigger={jumpTrigger}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            ariaLabel="Jump to date"
            className="p-0"
          >
            <div className="p-3">
              <Calendar
                mode="single"
                monthsToShow={1}
                showToday
                value={fromJSDate(anchor)}
                defaultMonth={{
                  year: anchor.getFullYear(),
                  month: anchor.getMonth(),
                }}
                onChange={(value) => handleJumpTo(value as CalendarDate | null)}
              />
            </div>
          </Popover>
          <button
            type="button"
            onClick={() => setAnchor(startOfWeek(new Date()))}
            className="text-xs underline underline-offset-2 hover:text-foreground text-muted-foreground"
          >
            Today
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-[var(--radius)] border border-border overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setViewMode("4weeks")}
              aria-pressed={viewMode === "4weeks"}
              className={`px-2 py-1 ${
                viewMode === "4weeks"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted-foreground/10"
              }`}
            >
              4 Weeks
            </button>
            <button
              type="button"
              onClick={() => setViewMode("week")}
              aria-pressed={viewMode === "week"}
              className={`px-2 py-1 border-l border-border ${
                viewMode === "week"
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted-foreground/10"
              }`}
            >
              Week
            </button>
          </div>
          <button
            type="button"
            onClick={() => step(1)}
            className="p-1 rounded hover:bg-muted-foreground/10"
            aria-label="Next period"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="overflow-auto flex-1">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-border bg-muted/50 sticky top-0 z-10">
          {WEEKDAYS.map((wd) => (
            <div
              key={wd}
              className="px-2 py-1.5 text-center text-xs font-semibold text-muted-foreground"
            >
              {wd}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 auto-rows-fr">
          {days.map((day) => {
            const key = format(day, "yyyy-MM-dd");
            const dayItems = itemsByDay.get(key) || [];
            const isToday = isSameDay(day, today);
            return (
              <div
                key={key}
                className="min-h-24 border-b border-r border-border p-1 align-top"
              >
                <div
                  className={`text-xs mb-1 text-right pr-1 ${
                    isToday
                      ? "font-bold text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {isToday ? (
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      {format(day, "d")}
                    </span>
                  ) : (
                    format(day, "d")
                  )}
                </div>
                <div className="space-y-1">
                  {groupByResident(dayItems).map(([residentName, items]) => {
                    if (items.length > RESIDENT_DAY_COLLAPSE_THRESHOLD) {
                      // Collapse a busy resident into a single popup trigger.
                      return (
                        <button
                          type="button"
                          key={`${key}-${residentName}`}
                          onClick={() =>
                            setPopup({
                              residentName,
                              dateLabel: format(day, "MMM d, yyyy"),
                              items,
                            })
                          }
                          title={`${residentName} — ${items.length} assessments (view all)`}
                          className="block w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] leading-tight bg-accent text-accent-foreground hover:opacity-90 font-medium"
                        >
                          {residentName} ({items.length})
                        </button>
                      );
                    }
                    return items.map((item) => {
                      const finished = isFinished(item.status_normalized);
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => handleItemClick(item)}
                          title={`${item.resident_name} — ${item.assessment_name || item.assessment_type}`}
                          className={`block w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] leading-tight text-white hover:opacity-90 ${
                            finished
                              ? "bg-[var(--color-success)]"
                              : "bg-[var(--color-primary,#0d9488)]"
                          }`}
                        >
                          {item.resident_name}
                        </button>
                      );
                    });
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Assessments with no parseable ARD */}
        {unscheduled.length > 0 && (
          <div className="p-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              No Assessment Reference Date ({unscheduled.length})
            </p>
            <div className="flex flex-wrap gap-1.5">
              {unscheduled.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  title={`${item.resident_name} — ${item.assessment_name || item.assessment_type}`}
                  className="inline-flex rounded bg-muted px-2 py-1 text-[11px] hover:bg-muted-foreground/10"
                >
                  {item.resident_name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Per-resident overflow popup. Rendered inside the calendar root (not a
          portal) so it stays within the widget's shadow root. */}
      {popup && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Assessments for ${popup.residentName}`}
          onClick={() => setPopup(null)}
        >
          <div
            className="max-h-full w-full max-w-sm overflow-auto rounded-[var(--radius)] border border-border bg-card shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-2">
              <div>
                <p className="text-sm font-semibold">{popup.residentName}</p>
                <p className="text-xs text-muted-foreground">
                  {popup.dateLabel} · {popup.items.length} assessments
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPopup(null)}
                className="p-1 rounded hover:bg-muted-foreground/10"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-2 space-y-1">
              {popup.items.map((item) => {
                const finished = isFinished(item.status_normalized);
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => {
                      handleItemClick(item);
                      setPopup(null);
                    }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted"
                  >
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${
                        finished
                          ? "bg-[var(--color-success)]"
                          : "bg-[var(--color-primary,#0d9488)]"
                      }`}
                    />
                    <span className="truncate">
                      {item.assessment_name || item.assessment_type}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
